import React, { createContext, useContext, ReactNode } from 'react';
import huTranslations from '../translations/hu';

// Define the shape of our translation context
interface TranslationContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
}

// Create the context with a default value
const TranslationContext = createContext<TranslationContextType>({
  t: () => '',
});

// Custom hook to use the translation context
export const useTranslation = () => useContext(TranslationContext);

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  // Function to get a nested value from an object using a dot-notation string
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((prev, curr) => {
      return prev && prev[curr] !== undefined ? prev[curr] : '';
    }, obj);
  };

  // Translation function
  const t = (key: string, params?: Record<string, string | number>): string => {
    // Get the translation from the Hungarian translations
    let translation = getNestedValue(huTranslations, key);
    
    // If the translation is not found, return the key
    if (!translation) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    
    // Replace parameters in the translation if any
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(`{${paramKey}}`, String(paramValue));
      });
    }
    
    return translation;
  };

  return (
    <TranslationContext.Provider value={{ t }}>
      {children}
    </TranslationContext.Provider>
  );
};
